import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import miniapp from './miniapp.messages.json';
import web from './web.messages.json';
import { LANGUAGES } from '../config.js';

/*
 * Translations.
 *
 * Two sources, merged per language:
 *   miniapp.messages.json — a verbatim copy of webapp/src/messages.json, so the
 *                           Mini App's wording is reused where it fits.
 *   web.messages.json     — strings written for this web UI. These win on any
 *                           shared key, because bot copy carries leading emoji
 *                           and line breaks that don't belong in web layout.
 *
 * The same t(key, vars) signature as webapp/src/i18n.js, so anything ported
 * from the Mini App keeps working.
 */

const SUPPORTED = LANGUAGES.map((l) => l.code);
const STORAGE_KEY = 'dq_lang';

const MESSAGES = Object.fromEntries(
  SUPPORTED.map((code) => [code, { ...(miniapp[code] ?? {}), ...(web[code] ?? {}) }])
);

/** Substitute {placeholders}; unknown keys fall back to English, then the key. */
export function translate(lang, key, vars = {}) {
  const message = MESSAGES[lang]?.[key] ?? MESSAGES.en?.[key] ?? key;
  return Object.entries(vars).reduce(
    (out, [name, value]) => out.replaceAll(`{${name}}`, String(value)),
    message
  );
}

/** Prefer a stored choice, then the browser's language, then English. */
function detectLanguage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;
  } catch {
    /* storage unavailable — fall through to the browser language */
  }
  const browser = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED.includes(browser) ? browser : 'en';
}

/*
 * <html lang> has exactly one owner.
 *
 * The innermost pinned provider wins while it's mounted (the marketing pages);
 * otherwise the user's own preference applies. Kept at module scope because
 * nested providers can't coordinate through effect ordering alone.
 */
const pinnedLanguages = [];
let preferredLanguage = 'en';

function applyDocumentLanguage() {
  document.documentElement.lang = pinnedLanguages.at(-1) ?? preferredLanguage;
}

const I18nContext = createContext(null);

/**
 * @param {string} [forceLang] Pin this subtree to one language and ignore the
 *   user's preference. Used by the marketing pages, which are English-only for
 *   launch — without it a Russian visitor would get English marketing copy with
 *   a Russian widget inside it. Nest it inside the app-wide provider.
 */
export function I18nProvider({ children, forceLang }) {
  const [stored, setLangState] = useState(detectLanguage);
  const lang = forceLang ?? stored;
  const isPinned = Boolean(forceLang);

  useEffect(() => {
    if (isPinned) {
      // A pinned subtree owns <html lang> while it is mounted: an English-only
      // page must not announce itself as Russian to search engines and screen
      // readers. Registering the pin (rather than writing the attribute here)
      // keeps nested providers from fighting over it — React runs child effects
      // before parent ones, so a direct write would just be overwritten.
      pinnedLanguages.push(lang);
      applyDocumentLanguage();
      return () => {
        const i = pinnedLanguages.lastIndexOf(lang);
        if (i !== -1) pinnedLanguages.splice(i, 1);
        applyDocumentLanguage();
      };
    }

    preferredLanguage = lang;
    applyDocumentLanguage();
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    return undefined;
  }, [lang, isPinned]);

  const setLang = useCallback((next) => {
    if (SUPPORTED.includes(next)) setLangState(next);
    // The backend also stores a language per account (POST /api/language), but
    // that endpoint is Telegram-auth only today. Wire it up alongside the rest
    // of the platform endpoints so the choice follows the user across devices.
  }, []);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      languages: LANGUAGES,
      t: (key, vars) => translate(lang, key, vars),
    }),
    [lang, setLang]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>');
  return ctx;
}

/**
 * Kick-off time, always 24-hour.
 *
 * The product shows 24-hour clocks everywhere (the design system, and the
 * backend's own `format_match_time`), so this doesn't defer to locale
 * conventions the way formatDate does — an en-US user would otherwise see
 * "08:00 PM" here and "20:00" in the match list.
 *
 * Datetimes now arrive with an explicit Z offset, so this converts to the
 * viewer's own timezone correctly. Note that Top Picks' `match_date` is a
 * calendar DATE, not an instant — kick-off comes from `match_start_utc`.
 */
export function formatMatchTime(value, lang) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(lang, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/**
 * Date + time together, 24-hour like everything else in the product.
 * Used for "graded at" / "generated at" style timestamps.
 */
export function formatDateTime(value, lang) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(lang, {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/** Locale-aware date helpers, so screens don't hand-roll formatting. */
export function formatDate(date, lang, options = { day: 'numeric', month: 'long' }) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(lang, options).format(d);
}
